import { useState } from "react";

interface MarketplaceProduct {
  id: number;
  name: string;
  price: number;
  image: string;
  seller: string;
  sellerFlag: string;
  factoryAddress: string;
}

interface Props {
  product: MarketplaceProduct;
  onClose: () => void;
}

interface Message {
  id: number;
  from: "buyer" | "seller";
  text: string;
  time: string;
}

const initialMessages: Message[] = [
  { id: 1, from: "seller", text: "Hello! Thank you for your interest in our product. How can I help you today?", time: "10:02 AM" },
  { id: 2, from: "seller", text: "We offer sample orders with fast UPS delivery. Feel free to ask about pricing, MOQ, or customization.", time: "10:02 AM" },
];

export default function MarketplaceMessageModal({ product, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [nameSet, setNameSet] = useState(false);

  const sellerReplies = [
    "Great question! Our minimum order is just 1 piece for samples.",
    "Yes, we can customize the packaging with your brand logo.",
    "Shipping usually takes 3–5 business days via UPS.",
    "We accept PayPal, credit card, and bank transfer.",
    "Our factory is certified ISO 9001. Quality is guaranteed!",
    "We can provide a certificate of origin and product test reports.",
    "For bulk orders (50+ pcs), we offer a 10% discount.",
    "Yes, we ship to all 30+ countries in our network.",
  ];

  const handleSend = () => {
    if (!input.trim()) return;
    const now = new Date();
    const time = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    const newMsg: Message = { id: Date.now(), from: "buyer", text: input.trim(), time };
    setMessages(prev => [...prev, newMsg]);
    setInput("");
    setTimeout(() => {
      const reply = sellerReplies[Math.floor(Math.random() * sellerReplies.length)];
      setMessages(prev => [...prev, { id: Date.now() + 1, from: "seller", text: reply, time }]);
    }, 1000 + Math.random() * 800);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  if (!nameSet) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-sm p-8 text-center" onClick={e => e.stopPropagation()}>
          <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
            <i className="ri-message-3-line text-amber-500 text-2xl"></i>
          </div>
          <h3 className="font-extrabold text-gray-900 text-lg mb-1">Message Seller</h3>
          <p className="text-gray-500 text-sm mb-5">Enter your name to start chatting with {product.sellerFlag} {product.seller}</p>
          <input
            value={buyerName}
            onChange={e => setBuyerName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && buyerName.trim()) setNameSet(true); }}
            placeholder="Your name"
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors mb-3"
          />
          <button
            onClick={() => { if (buyerName.trim()) setNameSet(true); }}
            className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            Start Chat
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md flex flex-col" style={{ height: "560px" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-sm truncate">{product.sellerFlag} {product.seller}</p>
            <p className="text-xs text-gray-400 truncate">{product.factoryAddress}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 bg-green-400 rounded-full inline-block"></span>
            <span className="text-xs text-green-600 font-semibold">Online</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer shrink-0">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Product context */}
        <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 shrink-0">
          <p className="text-xs text-amber-700 font-semibold truncate">
            <i className="ri-shopping-bag-3-line mr-1"></i>
            Inquiring about: {product.name} — ${product.price}
          </p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.from === "buyer" ? "justify-end" : "justify-start"}`}>
              {msg.from === "seller" && (
                <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full shrink-0 mr-2 mt-0.5">
                  <span className="text-xs">{product.sellerFlag}</span>
                </div>
              )}
              <div className={`max-w-[75%] ${msg.from === "buyer" ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.from === "buyer"
                    ? "bg-amber-400 text-white rounded-br-sm"
                    : "bg-gray-100 text-gray-800 rounded-bl-sm"
                }`}>
                  {msg.text}
                </div>
                <span className="text-xs text-gray-400 px-1">{msg.time}</span>
              </div>
              {msg.from === "buyer" && (
                <div className="w-7 h-7 flex items-center justify-center bg-amber-400 rounded-full shrink-0 ml-2 mt-0.5">
                  <span className="text-xs text-white font-bold">{buyerName.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="px-5 py-4 border-t border-gray-100 shrink-0">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className="flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="w-10 h-10 flex items-center justify-center bg-amber-400 hover:bg-amber-500 disabled:bg-gray-200 text-white rounded-full transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-send-plane-fill text-sm"></i>
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">Press Enter to send · Seller typically replies within 1 hour</p>
        </div>
      </div>
    </div>
  );
}
